<template>
    <div id="hints" class="columns">
        <div class="column col-auto">
            <div class="hint-header">
                <div>Zenith <span class="note">mm</span></div>
                <div class="crown-image"></div>
                <div>Incisal <span class="note">mm</span></div>
            </div>
            <div class="hint-header">
                <div>Width <span class="note">mm</span></div>
                <div class="original-value">&nbsp;</div>
            </div>
            <div class="hint-header">
                <div>Height <span class="note">mm</span></div>
                <div class="original-value">&nbsp;</div>
            </div>
            <div class="hint-header">
                <div>W/H <span class="note">%</span></div>
                <div class="original-value">&nbsp;</div>
            </div>
            <div class="hint-header">
                <div>Proportion</div>
                <div class="original-value">&nbsp;</div>
            </div>
        </div>

        <div class="column" v-for="r in stats" v-bind:key="r.tooth">
            <div class="hint">
                <div :class="getBackgroundZI(r.z)">{{ r.z.toFixed(1) }}</div>
                <div class="crown-image" >
                    <img v-if="r.tooth === 13" src="@/assets/crowns/13.svg" alt="13">
                    <img v-if="r.tooth === 12" src="@/assets/crowns/12.svg" alt="12">
                    <img v-if="r.tooth === 11" src="@/assets/crowns/11.svg" alt="11">
                    <img v-if="r.tooth === 21" src="@/assets/crowns/21.svg" alt="21">
                    <img v-if="r.tooth === 22" src="@/assets/crowns/22.svg" alt="22">
                    <img v-if="r.tooth === 23" src="@/assets/crowns/23.svg" alt="23">
                </div>
                <div :class="getBackgroundZI(r.i)">{{ r.i.toFixed(1) }}</div>
            </div>

            <div class="hint">
                <div>{{ r.w.toFixed(1) }}</div>
                <div class="original-value">{{ r.original.w.toFixed(1) }}</div>
            </div>

            <div class="hint">
                <div>{{ r.h.toFixed(1) }}</div>
                <div class="original-value">{{ r.original.h.toFixed(1) }}</div>
            </div>

            <div class="hint">
                <div :class="getBackgroundWHRatio(r)">{{ (r.w * 100 / r.h).toFixed() }}%</div>
                <div class="original-value">{{ (r.original.w * 100 / r.original.h).toFixed() }}%</div>
            </div>

            <div class="hint">
                <div :class="getBackgroundProportion(r)">{{ r.proportion.toFixed(2) }}</div>
                <div class="original-value">{{ r.original.proportion.toFixed(2) }}</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    computed: {
        pixelsPerMm () {
            let zenith = this.$store.state.zenithCoords[2]
            let incisalEdge = this.$store.state.incisalEdgeCoords[2]

            if (isNaN(this.$store.state.referenceLength)) {
                return NaN
            }

            if (zenith === undefined) {
                return NaN
            }

            if (incisalEdge === undefined) {
                return NaN
            }

            return (incisalEdge.y - zenith.y) / this.$store.state.referenceLength
        },

        stats () {
            return this.toothNumbers.map((number, index) => {
                const contourCoords = this.$store.state.contourCoords[index]
                const planCoords = this.$store.state.planCoords[index]

                const pixelsPerMm = this.pixelsPerMm

                let stats = {
                    tooth: number,
                    w: NaN,
                    h: NaN,

                    original: {
                        w: NaN,
                        h: NaN,

                        proportion: NaN,
                    },

                    z: NaN,
                    i: NaN,

                    proportion: NaN,
                }

                if (contourCoords === undefined || planCoords === undefined ||
                    this.$store.state.contourCoords[1] === undefined || this.$store.state.contourCoords[4] === undefined) {
                    return stats
                }

                const contourBoundingBox = _getBoundingBox(contourCoords)
                const planBoundingBox = _getBoundingBox(planCoords)

                stats.w = (planBoundingBox.xmax - planBoundingBox.xmin) / pixelsPerMm
                stats.h = (planBoundingBox.ymax - planBoundingBox.ymin) / pixelsPerMm

                stats.original.w = (contourBoundingBox.xmax - contourBoundingBox.xmin) / pixelsPerMm
                stats.original.h = (contourBoundingBox.ymax - contourBoundingBox.ymin) / pixelsPerMm

                stats.z = (contourBoundingBox.ymin - planBoundingBox.ymin) / pixelsPerMm
                stats.i = (planBoundingBox.ymax - contourBoundingBox.ymax) / pixelsPerMm

                let refBoundingBox, origRefBoundingBox
                if (number < 19) { // 13, 12, 11
                    refBoundingBox = _getBoundingBox(this.$store.state.planCoords[1])
                    origRefBoundingBox = _getBoundingBox(this.$store.state.contourCoords[1])
                } else { // 21, 22, 23
                    refBoundingBox = _getBoundingBox(this.$store.state.planCoords[4])
                    origRefBoundingBox = _getBoundingBox(this.$store.state.contourCoords[4])
                }

                switch (number) {
                    case 12: // 12 and 22 are the reference proportions
                    case 22:
                        stats.proportion = 1.0
                        stats.original.proportion = 1.0
                        break
                    default:
                        stats.proportion = (planBoundingBox.xmax - planBoundingBox.xmin) / (refBoundingBox.xmax - refBoundingBox.xmin)
                        stats.original.proportion = (contourBoundingBox.xmax - contourBoundingBox.xmin) / (origRefBoundingBox.xmax - origRefBoundingBox.xmin)
                }

                return stats
            })
        }
    },
    name: "StatsPanel",
    data () {
        return {
            toothNumbers: [13, 12, 11, 21, 22, 23]
        }
    },
    methods: {
        getBackgroundZI(value) {
            return {
                'bg-success': value > 0,
                'bg-error': value < 0
            }
        },

        getBackgroundWHRatio(r) {
            switch (r.tooth) {
                case 11:
                case 21:
                    return { 'bg-warning': (r.w / r.h < 0.6) || (r.w / r.h > 0.9) }
                case 12:
                case 22:
                    return { 'bg-warning': (r.w / r.h < 0.6) || (r.w / r.h > 0.9) }
                case 13:
                case 23:
                    return {}
            }
        },

        getBackgroundProportion(r) {
            switch (r.tooth) {
                case 11:
                case 21:
                    return { 'bg-warning': (r.proportion < 1.5) || (r.proportion > 1.7) }
                case 13:
                case 23:
                    return { 'bg-warning': (r.proportion < 0.5) || (r.proportion > 0.7) }
                default:
                    return {}
            }
        }
    }
}

function _getBoundingBox (coords) {
    return coords.reduce((acc, curr) => {
        if (curr.x < acc.xmin) {
            acc.xmin = curr.x
        }
        if (curr.x > acc.xmax) {
            acc.xmax = curr.x
        }
        if (curr.y < acc.ymin) {
            acc.ymin = curr.y
        }
        if (curr.y > acc.ymax) {
            acc.ymax = curr.y
        }

        return acc
    }, {
        xmin: coords[0].x,
        ymin: coords[0].y,
        xmax: coords[0].x,
        ymax: coords[0].y
    })
}
</script>

<style scoped lang="scss">
#hints {
    font-size: 90%;
    padding: 1rem;

    .hint, .hint-header {
        padding: 0.1rem;

        .crown-image {
            height: 2rem;

            img {
                width: 30px;
            }
        }

        .original-value, .note {
            color: #bbb;
            font-size: smaller;
        }
    }

    .hint {
        text-align: center;
    }

    .hint:nth-child(2n) {
        background-color: #f6f6f6
    }

    .hint:nth-child(2n+1) {
        background-color: #fbfbfb
    }
}
</style>